import type { RdHoursWeekdayName } from "../../../shared/api-types";
import type { DraftSource } from "./rd-hours-client";
import {
  RD_HOURS_DAYS,
  addBreak,
  addHour,
  copyDay,
  createBlock,
  dayName,
  deleteBlock,
  moveBreak,
  rangeLabel,
  removeBreak,
  sameDay,
  setBlockRange,
  setBreakLength,
  timeLabel,
  type DayEdit,
  type HoursDay,
  type HoursWeek,
} from "./rd-hours-editor";

// The Availability editor's draft (NO-878): what is on screen against what was
// loaded, the selected block, the copy targets, and the live-region line each
// edit announces. A pure reducer, relative imports only (`tsx --test`).

export type RdHoursMode = "template" | "week";

export interface BlockRef {
  day: RdHoursWeekdayName;
  index: number;
}

export interface DraftState {
  mode: RdHoursMode;
  source: DraftSource;
  days: HoursWeek;
  /** One week: the days the save replaces (the stored ones plus any edited). */
  changed: RdHoursWeekdayName[];
  /** Days edited since the source loaded; their visits are re-tested here. */
  edited: RdHoursWeekdayName[];
  timeZone: string;
  selection: BlockRef | null;
  copyTo: RdHoursWeekdayName[];
  announcement: string;
}

export type DraftAction =
  | { type: "rebase"; source: DraftSource }
  | { type: "discard" }
  | { type: "select"; block: BlockRef | null }
  | { type: "add_hour"; day: RdHoursWeekdayName; hourStart: number }
  | { type: "create"; day: RdHoursWeekdayName; from: number; to: number }
  | { type: "set_range"; block: BlockRef; start: number; end: number }
  | { type: "delete"; block: BlockRef }
  | { type: "add_break" }
  | { type: "move_break"; delta: number }
  | { type: "break_length"; minutes: 30 | 60 }
  | { type: "remove_break" }
  | { type: "toggle_copy"; day: RdHoursWeekdayName }
  | { type: "apply_copy" }
  | { type: "time_zone"; timeZone: string };

export function initialDraft(mode: RdHoursMode, source: DraftSource): DraftState {
  return {
    mode,
    source,
    days: source.days,
    changed: [...source.changed],
    edited: [],
    timeZone: source.timeZone,
    selection: null,
    copyTo: [],
    announcement: "",
  };
}

/** A save's stored hours become the baseline while `edited` stays: the server
 * flags on the visits still describe the pre-save week until a read succeeds,
 * so the days just saved keep being tested against the hours on screen. */
function rebase(state: DraftState, source: DraftSource): DraftState {
  return {
    ...state,
    source,
    days: source.days,
    changed: [...source.changed],
    timeZone: source.timeZone,
  };
}

function inOrder(days: readonly RdHoursWeekdayName[]): RdHoursWeekdayName[] {
  return RD_HOURS_DAYS.filter((day) => days.includes(day));
}

function withDays(
  state: DraftState,
  days: readonly RdHoursWeekdayName[],
  read: (day: RdHoursWeekdayName) => HoursDay
): DraftState {
  const next = { ...state.days };

  for (const day of days) next[day] = read(day);

  const touched =
    state.mode === "week" ? inOrder([...state.changed, ...days]) : state.changed;

  return {
    ...state,
    days: next,
    changed: touched,
    edited: inOrder([...state.edited, ...days]),
  };
}

function landOn(
  state: DraftState,
  day: RdHoursWeekdayName,
  edit: DayEdit | null,
  verb: string
): DraftState {
  if (edit === null) return state;

  const block = edit.day[edit.index];
  const announcement =
    block === undefined ? "" : `${dayName(day)} ${verb} ${rangeLabel(block)}`;
  const next = withDays(state, [day], () => edit.day);
  const sameBlock = sameRef(state.selection, { day, index: edit.index });

  return {
    ...next,
    selection: block === undefined ? null : { day, index: edit.index },
    copyTo: sameBlock ? state.copyTo : [],
    announcement,
  };
}

function sameRef(a: BlockRef | null, b: BlockRef | null): boolean {
  if (a === null || b === null) return a === b;

  return a.day === b.day && a.index === b.index;
}

function selectedDay(state: DraftState): { ref: BlockRef; day: HoursDay } | null {
  const ref = state.selection;
  if (ref === null) return null;

  const day = state.days[ref.day];

  return day[ref.index] === undefined ? null : { ref, day };
}

function editBreak(
  state: DraftState,
  change: (day: HoursDay, index: number) => HoursDay,
  describe: (day: HoursDay, index: number, name: string) => string
): DraftState {
  const selected = selectedDay(state);
  if (selected === null) return state;

  const { ref, day } = selected;
  const nextDay = change(day, ref.index);

  if (nextDay === day) return state;

  return {
    ...withDays(state, [ref.day], () => nextDay),
    announcement: describe(nextDay, ref.index, dayName(ref.day)),
  };
}

function breakLine(day: HoursDay, index: number, name: string): string {
  const lunch = day[index]?.lunchBreak;
  if (!lunch) return `${name} has no lunch break`;

  return `${name} lunch break ${rangeLabel({ start: lunch.start, end: lunch.start + lunch.minutes })}`;
}

function deleteRef(state: DraftState, ref: BlockRef): DraftState {
  const block = state.days[ref.day][ref.index];
  if (block === undefined) return state;

  const next = withDays(state, [ref.day], (day) =>
    deleteBlock(state.days[day], ref.index)
  );
  const wasSelected = state.selection !== null && state.selection.day === ref.day;

  return {
    ...next,
    selection: wasSelected ? null : state.selection,
    copyTo: wasSelected ? [] : state.copyTo,
    announcement: `${dayName(ref.day)} ${rangeLabel(block)} removed`,
  };
}

function toggleCopy(state: DraftState, day: RdHoursWeekdayName): DraftState {
  if (state.selection === null || state.selection.day === day) return state;

  const copyTo = state.copyTo.includes(day)
    ? state.copyTo.filter((current) => current !== day)
    : inOrder([...state.copyTo, day]);

  return { ...state, copyTo };
}

function applyCopy(state: DraftState): DraftState {
  const source = state.selection;
  if (source === null || state.copyTo.length === 0) return state;

  const copied = copyDay(state.days, source.day, state.copyTo);
  const next = withDays(state, state.copyTo, (day) => copied[day]);

  return {
    ...next,
    copyTo: [],
    announcement: `Copied ${dayName(source.day)} to ${state.copyTo.map(dayName).join(", ")}`,
  };
}

function selectBlock(state: DraftState, block: BlockRef | null): DraftState {
  const same = sameRef(state.selection, block);

  return { ...state, selection: block, copyTo: same ? state.copyTo : [] };
}

function rangeEdit(
  state: DraftState,
  action: Extract<DraftAction, { type: "set_range" }>
) {
  const { block } = action;
  const edit = setBlockRange(
    state.days[block.day],
    block.index,
    action.start,
    action.end
  );

  return landOn(state, block.day, edit, "now");
}

function breakEdit(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case "add_break":
      return editBreak(state, addBreak, breakLine);
    case "move_break":
      return editBreak(
        state,
        (day, index) => moveBreak(day, index, action.delta),
        breakLine
      );
    case "break_length":
      return editBreak(
        state,
        (day, index) => setBreakLength(day, index, action.minutes),
        breakLine
      );
    default:
      return editBreak(
        state,
        removeBreak,
        (_day, _index, name) => `${name} lunch break removed`
      );
  }
}

export function draftReducer(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case "rebase":
      return rebase(state, action.source);
    case "discard":
      return { ...initialDraft(state.mode, state.source), edited: state.edited };
    case "select":
      return selectBlock(state, action.block);
    case "add_hour":
      return landOn(
        state,
        action.day,
        addHour(state.days[action.day], action.hourStart),
        "now"
      );
    case "create":
      return landOn(
        state,
        action.day,
        createBlock(state.days[action.day], action.from, action.to),
        "now"
      );
    case "set_range":
      return rangeEdit(state, action);
    case "delete":
      return deleteRef(state, action.block);
    case "toggle_copy":
      return toggleCopy(state, action.day);
    case "apply_copy":
      return applyCopy(state);
    case "time_zone":
      return { ...state, timeZone: action.timeZone, announcement: "" };
    default:
      return breakEdit(state, action);
  }
}

export function isDraftDirty(state: DraftState): boolean {
  if (state.timeZone !== state.source.timeZone) return true;

  if (inOrder(state.changed).join() !== inOrder(state.source.changed).join())
    return true;

  return RD_HOURS_DAYS.some((day) => !sameDay(state.days[day], state.source.days[day]));
}

/** The panel's lunch break tag time: "12 PM to 12:30 PM". */
export function lunchRangeLabel(start: number, minutes: number): string {
  return `${timeLabel(start)} to ${timeLabel(start + minutes)}`;
}
